import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MapPin, Loader2, LocateFixed } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface PlacePrediction {
  place_id: string;
  description: string;
  display_name: string;
  lat: number;
  lng: number;
}

interface LocationAutocompleteProps {
  value: string;
  onChange?: (value: string) => void;
  onSelect: (location: { name: string; lat: number; lng: number }) => void;
  placeholder?: string;
  className?: string;
}

export function LocationAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "City or ZIP code",
  className,
}: LocationAutocompleteProps) {
  const [localValue, setLocalValue] = useState(value);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [geolocationError, setGeolocationError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInternalChangeRef = useRef(false);
  const isUserTypingRef = useRef(false);
  const abortCounterRef = useRef(0);

  useEffect(() => {
    if (!isInternalChangeRef.current) {
      setLocalValue(value);
      setSuggestions([]);
      setOpen(false);
    }
    isInternalChangeRef.current = false;
  }, [value]);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    const currentRequest = ++abortCounterRef.current;
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('places-autocomplete', {
        body: { query },
      });

      if (currentRequest !== abortCounterRef.current) return;

      if (error) {
        console.error('Places autocomplete error:', error);
        setSuggestions([]);
        return;
      }

      const predictions = data?.predictions || [];
      setSuggestions(predictions);
      if (predictions.length > 0) setOpen(true);
    } catch (error) {
      console.error('Places autocomplete error:', error);
      setSuggestions([]);
    } finally {
      if (currentRequest === abortCounterRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleUseCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setGeolocationError('Geolocation not supported');
      return;
    }

    setIsLocating(true);
    setGeolocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const { data, error } = await supabase.functions.invoke('places-autocomplete', {
            body: { type: 'reverse', lat: latitude, lng: longitude },
          });

          if (error) throw error;

          const locationName = data?.name || 'Current Location';
          onChange?.(locationName);
          onSelect({
            name: locationName,
            lat: data?.lat || latitude,
            lng: data?.lng || longitude,
          });
        } catch {
          onChange?.('Current Location');
          onSelect({ name: 'Current Location', lat: latitude, lng: longitude });
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        setIsLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGeolocationError('Location access denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setGeolocationError('Location unavailable');
            break;
          case error.TIMEOUT:
            setGeolocationError('Location request timed out');
            break;
          default:
            setGeolocationError('Could not get location');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [onChange, onSelect]);

  useEffect(() => {
    if (!isUserTypingRef.current) return;
    isUserTypingRef.current = false;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (localValue.length >= 2) {
      debounceRef.current = setTimeout(() => fetchSuggestions(localValue), 300);
    } else {
      setSuggestions([]);
      setOpen(false);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localValue, fetchSuggestions]);

  const handleSelect = (prediction: PlacePrediction) => {
    const name = prediction.display_name;
    isInternalChangeRef.current = true;
    setLocalValue(name);
    setSuggestions([]);
    setOpen(false);
    onChange?.(name);
    onSelect({ name, lat: prediction.lat, lng: prediction.lng });
    inputRef.current?.blur();
  };

  return (
    <div className={cn("flex gap-1", className)}>
      <div className="relative flex-1">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={localValue}
          onChange={(e) => {
            isInternalChangeRef.current = true;
            isUserTypingRef.current = true;
            setLocalValue(e.target.value);
            onChange?.(e.target.value);
            setGeolocationError(null);
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onBlur={() => setOpen(false)}
          className="pl-10 pr-8"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        )}
        {open && suggestions.length > 0 && (
          <div
            className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover text-popover-foreground shadow-md"
            onMouseDown={(e) => e.preventDefault()}
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {suggestions.map((prediction) => (
                    <CommandItem
                      key={prediction.place_id}
                      value={prediction.description}
                      onSelect={() => handleSelect(prediction)}
                      className="cursor-pointer"
                    >
                      <MapPin className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="truncate">{prediction.display_name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleUseCurrentLocation}
              disabled={isLocating}
              aria-label="Use my current location"
              className={cn(
                "shrink-0",
                geolocationError && "border-destructive text-destructive"
              )}
            >
              {isLocating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LocateFixed className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {geolocationError || "Use my current location"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
